/**
 * legalConfig.ts — per-workspace Legal configuration model (Wave 4,
 * security-audit-full-2026-10-25 Slice D remediation + owner directive
 * LEGAL_SETTINGS_SPEC.md, 2026-10-25).
 *
 * ALL legal content (policies, terms, disclaimers, consent texts, links,
 * postal address, legal documents) is workspace-configured in Workspace
 * Settings → Legal and stored server-stamped in the `workspaceLegal`
 * collection (one doc per workspaceId). NOTHING in this module — or
 * anywhere in the legal layer — hardcodes a brand. When a workspace has
 * not configured an item, consumers render a NEUTRAL unbranded state.
 *
 * This module is pure (no React, no Firestore) so the server routes and
 * the client share one definition of the shape, the completeness rules,
 * URL validation and the age gate, and so it can be unit-tested directly.
 */

export type LegalDocType = 'privacy' | 'terms' | 'refund' | 'cookie' | 'accessibility';

export type LegalDisclosureKey =
  | 'nonAttorneyDisclaimer'
  | 'biometricNotice'
  | 'biometricSchedule'
  | 'esignConsent'
  | 'marketingConsent'
  | 'referrerCommissionDisclosure';

export interface LegalDocBody {
  /** Sanitised rich-text HTML (sanitised on save, server-side AND client-side). */
  html: string;
  /** Monotonic per-document version; bumps when the body changes. */
  version: number;
  /** ISO timestamp of the last change. */
  updatedAt?: string;
}

export interface LegalDisclosure extends LegalDocBody {}

export interface LegalDocumentSlotValue {
  storagePath: string;
  fileName: string;
  version: number;
  /** ISO date (yyyy-mm-dd) the document takes effect. */
  effectiveDate: string;
  uploadedBy: string;
  uploadedAt: string;
  active: boolean;
}

export interface WorkspaceLegalConfig {
  workspaceId: string;
  /** Link fields (validated http/https, or '' when unset). */
  links: {
    privacyUrl: string;
    termsUrl: string;
    refundUrl: string;
    cookieUrl: string;
    accessibilityUrl: string;
    privacyRequestUrl: string;
    doNotSellUrl: string;
  };
  /** Legal / privacy contact email ('' when unset). */
  privacyContactEmail: string;
  /** Postal address — drives the CAN-SPAM email footer. '' when unset. */
  postalAddress: string;
  /** Rich-text policy bodies, keyed by document type. */
  documents: Partial<Record<LegalDocType, LegalDocBody>>;
  /** Rich-text disclosure / consent texts. */
  disclosures: Partial<Record<LegalDisclosureKey, LegalDisclosure>>;
  /** Uploaded legal documents, keyed by slot (see LEGAL_DOCUMENT_SLOTS). */
  documentSlots: Partial<Record<string, LegalDocumentSlotValue>>;
  updatedAt?: string;
  updatedBy?: string;
}

export const LEGAL_DOC_TYPES: { key: LegalDocType; label: string; path: string; why: string }[] = [
  { key: 'privacy', label: 'Privacy Policy', path: '/privacy', why: 'CCPA/CPRA, GDPR, PIPEDA/Law 25 and Jamaica DPA all require a specific notice at or before collection: what is collected, why, who receives it, retention, rights and a contact.' },
  { key: 'terms', label: 'Terms of Service', path: '/terms', why: 'An enforceable fee/service contract needs published terms that users actually accept (clickwrap), including fees, refunds and that the business is not a law firm.' },
  { key: 'refund', label: 'Refund Policy', path: '/refund-policy', why: 'Fee disputes are the top complaint pattern in this industry; a published refund schedule (visa denial, withdrawal, stage reached) is required for an enforceable payment flow and state immigration-consultant regimes.' },
  { key: 'cookie', label: 'Cookie Policy', path: '/cookies', why: 'GDPR/UK PECR and Quebec Law 25 require classifying browser storage; CPRA requires notice and honouring opt-out signals such as GPC.' },
  { key: 'accessibility', label: 'Accessibility Statement', path: '/accessibility', why: 'ADA Title III / California Unruh exposure is assessed against WCAG 2.1 AA; a statement with a feedback contact is the expected public commitment.' },
];

export const LEGAL_LINK_FIELDS: { key: keyof WorkspaceLegalConfig['links']; label: string; why: string; optional?: boolean }[] = [
  { key: 'privacyUrl', label: 'Privacy Policy URL', why: 'If the policy is hosted externally, this URL is what footer links, clickwrap and the privacy notice point to.' },
  { key: 'termsUrl', label: 'Terms of Service URL', why: 'Clickwrap must link the exact terms being accepted; a dead link makes acceptance unenforceable.' },
  { key: 'refundUrl', label: 'Refund Policy URL', why: 'Payment screens must link the refund terms before money moves.' },
  { key: 'cookieUrl', label: 'Cookie Policy URL', why: 'Cookie/storage notice destination for footer links.' },
  { key: 'accessibilityUrl', label: 'Accessibility Statement URL', why: 'Accessibility feedback route expected under ADA/AODA practice.' },
  { key: 'privacyRequestUrl', label: 'Data Rights / Privacy Request URL', why: 'Data subjects need a reachable request channel (access, deletion, correction). The in-app /privacy-request page can be used.' },
  { key: 'doNotSellUrl', label: 'Do Not Sell/Share URL (optional)', why: 'CCPA/CPRA requires a "Do Not Sell or Share My Personal Information" link where sale/share occurs.', optional: true },
];

export const LEGAL_DISCLOSURES: { key: LegalDisclosureKey; label: string; why: string }[] = [
  { key: 'nonAttorneyDisclaimer', label: 'Non-attorney disclaimer', why: 'State immigration-consultant / notario laws (e.g. CA Bus. & Prof. Code §§22440–22448, NY GBL §460) require a non-attorney business charging for immigration-adjacent help to state it is not a law firm and cannot give legal advice.' },
  { key: 'biometricNotice', label: 'Biometric (identity verification) consent notice', why: 'Illinois BIPA §15(b): before capture, the subject must be told in writing that biometric data is collected, the purpose and the term, and give a written release. TX CUBI and WA law are similar.' },
  { key: 'biometricSchedule', label: 'Biometric retention & destruction schedule', why: 'Illinois BIPA §15(a): a publicly available written retention schedule and destruction guidelines (destroy at purpose-end or within 3 years of last interaction, whichever first).' },
  { key: 'esignConsent', label: 'E-sign consent text', why: 'ESIGN §7001(c) / UETA: the signer must consent to electronic records, be told how to obtain paper copies and how to withdraw, and the act must be provable (intent + attribution + retained record).' },
  { key: 'marketingConsent', label: 'Email / marketing consent text', why: 'CAN-SPAM and Canada CASL require identifiable senders, consent for commercial messages and a working unsubscribe honoured promptly.' },
  { key: 'referrerCommissionDisclosure', label: 'Referrer commission disclosure', why: 'FTC Act §5 / state UDAP: a paid referral interest in the applicant enrolling is material. Disclosure states existence and nature only — amounts are NEVER shown to applicants (standing product rule).' },
];

export const LEGAL_DOCUMENT_SLOTS: { key: string; label: string; why: string; public?: boolean }[] = [
  { key: 'privacyPolicy', label: 'Privacy Policy (document)', why: 'Signed/effective PDF of the published privacy policy — the version of record for consent disputes.', public: true },
  { key: 'termsOfService', label: 'Terms of Service (document)', why: 'Version of record of the terms users accepted.', public: true },
  { key: 'refundPolicy', label: 'Refund Policy (document)', why: 'Version of record of the refund schedule shown at payment.', public: true },
  { key: 'cookiePolicy', label: 'Cookie Policy (document)', why: 'Version of record of the storage/cookie notice.', public: true },
  { key: 'accessibilityStatement', label: 'Accessibility Statement (document)', why: 'Published WCAG conformance statement / audit summary.', public: true },
  { key: 'biometricSchedule', label: 'Biometric Retention & Destruction Schedule', why: 'BIPA §15(a) requires this schedule to exist in writing and be available to subjects.', public: true },
  { key: 'dpaTemplate', label: 'Data Processing Addendum (DPA) template', why: 'GDPR Art. 28 / CPRA service-provider contracts require written processor terms with sub-processors.' },
  { key: 'consentFormTemplate', label: 'Consent Form template', why: 'Paper/offline consent capture must match the in-app consent versions.' },
  { key: 'esignConsent', label: 'E-Sign Consent disclosure (document)', why: 'ESIGN §7001(c) consent disclosure of record.' },
  { key: 'immigrationBond', label: 'Immigration Consultant Bond / insurance certificate', why: 'CA Immigration Consultants Act requires a surety bond (counsel to confirm the current amount); other states have parallel registration/insurance duties.' },
];

/** Empty, neutral config — the state every unconfigured workspace renders. */
export function emptyLegalConfig(workspaceId: string): WorkspaceLegalConfig {
  return {
    workspaceId,
    links: { privacyUrl: '', termsUrl: '', refundUrl: '', cookieUrl: '', accessibilityUrl: '', privacyRequestUrl: '', doNotSellUrl: '' },
    privacyContactEmail: '',
    postalAddress: '',
    documents: {},
    disclosures: {},
    documentSlots: {},
  };
}

/** Defensive normalisation of an untrusted stored/loaded config object. */
export function normaliseLegalConfig(raw: any, workspaceId: string): WorkspaceLegalConfig {
  const base = emptyLegalConfig(workspaceId);
  if (!raw || typeof raw !== 'object') return base;
  const links = { ...base.links };
  for (const f of LEGAL_LINK_FIELDS) {
    const v = raw.links?.[f.key];
    if (typeof v === 'string') links[f.key] = v.trim();
  }
  const documents: WorkspaceLegalConfig['documents'] = {};
  for (const d of LEGAL_DOC_TYPES) {
    const doc = raw.documents?.[d.key];
    if (doc && typeof doc.html === 'string') {
      documents[d.key] = { html: doc.html, version: Number(doc.version) || 1, updatedAt: typeof doc.updatedAt === 'string' ? doc.updatedAt : undefined };
    }
  }
  const disclosures: WorkspaceLegalConfig['disclosures'] = {};
  for (const d of LEGAL_DISCLOSURES) {
    const doc = raw.disclosures?.[d.key];
    if (doc && typeof doc.html === 'string') {
      disclosures[d.key] = { html: doc.html, version: Number(doc.version) || 1, updatedAt: typeof doc.updatedAt === 'string' ? doc.updatedAt : undefined };
    }
  }
  const documentSlots: WorkspaceLegalConfig['documentSlots'] = {};
  for (const s of LEGAL_DOCUMENT_SLOTS) {
    const slot = raw.documentSlots?.[s.key];
    if (slot && typeof slot.storagePath === 'string' && slot.storagePath) {
      documentSlots[s.key] = {
        storagePath: slot.storagePath,
        fileName: typeof slot.fileName === 'string' ? slot.fileName : '',
        version: Number(slot.version) || 1,
        effectiveDate: typeof slot.effectiveDate === 'string' ? slot.effectiveDate : '',
        uploadedBy: typeof slot.uploadedBy === 'string' ? slot.uploadedBy : '',
        uploadedAt: typeof slot.uploadedAt === 'string' ? slot.uploadedAt : '',
        active: slot.active !== false,
      };
    }
  }
  return {
    workspaceId,
    links,
    privacyContactEmail: typeof raw.privacyContactEmail === 'string' ? raw.privacyContactEmail.trim() : '',
    postalAddress: typeof raw.postalAddress === 'string' ? raw.postalAddress : '',
    documents,
    disclosures,
    documentSlots,
    updatedAt: typeof raw.updatedAt === 'string' ? raw.updatedAt : undefined,
    updatedBy: typeof raw.updatedBy === 'string' ? raw.updatedBy : undefined,
  };
}

export function isValidLegalUrl(value: string): boolean {
  const v = (value || '').trim();
  if (!v) return true; // empty = unset, allowed
  try {
    const u = new URL(v);
    return u.protocol === 'https:' || u.protocol === 'http:';
  } catch {
    return false;
  }
}

export function isValidEmailAddress(value: string): boolean {
  const v = (value || '').trim();
  if (!v) return true;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
}

export interface LegalCompletenessItem {
  key: string;
  label: string;
  configured: boolean;
  why: string;
}

/** Configured/Missing checklist shown at the top of the Legal settings tab. */
export function legalCompleteness(config: WorkspaceLegalConfig): LegalCompletenessItem[] {
  const items: LegalCompletenessItem[] = [];
  for (const d of LEGAL_DOC_TYPES) {
    const hasBody = !!config.documents[d.key]?.html?.trim();
    const slotKey = { privacy: 'privacyPolicy', terms: 'termsOfService', refund: 'refundPolicy', cookie: 'cookiePolicy', accessibility: 'accessibilityStatement' }[d.key];
    const hasDoc = !!config.documentSlots[slotKey]?.active;
    const linkKey = { privacy: 'privacyUrl', terms: 'termsUrl', refund: 'refundUrl', cookie: 'cookieUrl', accessibility: 'accessibilityUrl' }[d.key] as keyof WorkspaceLegalConfig['links'];
    items.push({ key: `doc:${d.key}`, label: d.label, configured: hasBody || hasDoc || !!config.links[linkKey], why: d.why });
  }
  for (const d of LEGAL_DISCLOSURES) {
    items.push({ key: `disclosure:${d.key}`, label: d.label, configured: !!config.disclosures[d.key]?.html?.trim(), why: d.why });
  }
  items.push({ key: 'privacyContactEmail', label: 'Legal / privacy contact email', configured: !!config.privacyContactEmail, why: 'Every privacy regime requires a reachable privacy contact; rights requests need a monitored destination.' });
  items.push({ key: 'postalAddress', label: 'Postal address (email footer)', configured: !!config.postalAddress.trim(), why: 'CAN-SPAM requires a valid physical postal address in commercial email.' });
  const bond = config.documentSlots['immigrationBond'];
  items.push({ key: 'slot:immigrationBond', label: 'Immigration Consultant Bond / insurance certificate', configured: !!bond?.active, why: LEGAL_DOCUMENT_SLOTS.find(s => s.key === 'immigrationBond')!.why });
  return items;
}

/**
 * What a public legal page should render for a document type, in priority
 * order: active uploaded document → rich-text body → external link → none
 * (neutral state). Returns version info so consent records can store the
 * exact version shown.
 */
export function resolveLegalDocument(
  config: WorkspaceLegalConfig,
  docType: LegalDocType,
): { source: 'document' | 'body' | 'link' | 'none'; html?: string; url?: string; storagePath?: string; fileName?: string; version: number } {
  const slotKey = { privacy: 'privacyPolicy', terms: 'termsOfService', refund: 'refundPolicy', cookie: 'cookiePolicy', accessibility: 'accessibilityStatement' }[docType];
  const slot = config.documentSlots[slotKey];
  if (slot?.active && slot.storagePath) {
    return { source: 'document', storagePath: slot.storagePath, fileName: slot.fileName, version: slot.version };
  }
  const body = config.documents[docType];
  if (body?.html?.trim()) {
    return { source: 'body', html: body.html, version: body.version };
  }
  const linkKey = { privacy: 'privacyUrl', terms: 'termsUrl', refund: 'refundUrl', cookie: 'cookieUrl', accessibility: 'accessibilityUrl' }[docType] as keyof WorkspaceLegalConfig['links'];
  const url = config.links[linkKey];
  if (url) return { source: 'link', url, version: 0 };
  return { source: 'none', version: 0 };
}

/** The active version stamp for a legal text, for consent records. */
export function legalTextVersion(config: WorkspaceLegalConfig, kind: LegalDocType | LegalDisclosureKey): number {
  const doc = (config.documents as any)[kind] || (config.disclosures as any)[kind];
  return doc && typeof doc.version === 'number' ? doc.version : 0;
}

/**
 * Stable numeric fingerprint of a legal text that carries no version
 * counter of its own (e.g. program terms & conditions). Used in
 * signature-evidence records so the exact text signed can be proven
 * later. Same text → same value; any change → different value.
 */
export function legalContentHash(text: string): number {
  const s = String(text || '');
  if (!s.trim()) return 0;
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  return Math.abs(h);
}

// ─── Age gate (Slice D D-13) ────────────────────────────────────────────────

/** Whole years of age at a given date. Returns null for unparseable DOBs. */
export function computeAgeYears(dob: string | undefined | null, at: Date = new Date()): number | null {
  if (!dob || typeof dob !== 'string') return null;
  const m = dob.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) {
    const parsed = new Date(dob);
    if (Number.isNaN(parsed.getTime())) return null;
    return computeAgeYears(parsed.toISOString().slice(0, 10), at);
  }
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  let age = at.getFullYear() - y;
  const monthDiff = at.getMonth() + 1 - mo;
  if (monthDiff < 0 || (monthDiff === 0 && at.getDate() < d)) age -= 1;
  return age;
}

/** Default minimum age when a program does not configure one (Slice D D-13). */
export const DEFAULT_MINIMUM_AGE = 18;

/** The minimum age for a program: its configured value, else the default. */
export function minimumAgeForProgram(program: any): number {
  const v = program?.minimumAge;
  if (typeof v === 'number' && Number.isFinite(v) && v >= 0) return v;
  return DEFAULT_MINIMUM_AGE;
}

/**
 * Suppression decision shared by every sender (bulk jobs, automations).
 * A suppression doc may carry `workspaces: string[]` (scoped) — an
 * empty/absent list means the suppression predates scoping, or is
 * global, and must be honoured everywhere: never send to a suppressed
 * address on a technicality.
 */
export function isSuppressedForWorkspace(suppData: any, workspaceId?: string): boolean {
  if (!suppData) return false;
  const ws = suppData.workspaces;
  if (!Array.isArray(ws) || ws.length === 0) return true;
  if (!workspaceId) return true;
  return ws.includes(workspaceId);
}

export function ageGateCheck(
  dob: string | undefined | null,
  program: any,
  at: Date = new Date(),
): { ok: boolean; age: number | null; minimumAge: number; message?: string } {
  const minimumAge = minimumAgeForProgram(program);
  const age = computeAgeYears(dob, at);
  if (age === null) {
    return { ok: false, age, minimumAge, message: 'Please enter a valid date of birth.' };
  }
  if (age < minimumAge) {
    return {
      ok: false,
      age,
      minimumAge,
      message: `Applicants must be at least ${minimumAge} years old for this program. Based on the date of birth entered, the applicant is ${age}. If this is a mistake, please correct the date of birth; otherwise this program is not available.`,
    };
  }
  return { ok: true, age, minimumAge };
}
